import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
    considerationTypesByIdState,
    disconnectedDataKeysState,
    equipmentTypesByIdState,
    infoTypesByIdState,
    nodeValidationState,
} from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { AppNodeType, ConsiderationTypeId, EquipmentTypeId, InfoTypeId, SelectOption, SpaceId } from '@rock/types';
import { isOfferableType } from '@rock/types';
import { useAtomValue } from 'jotai';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { type FC, type PropsWithChildren, useContext, useMemo } from 'react';
import { FlowParamsContext } from '../app/FlowInitializer';
import { getItemTypeColors } from '../util/getItemTypeColors';

export const NodeContainer: FC<PropsWithChildren> = ({ children }) => (
    <div className="text-center cursor-pointer w-xs relative">{children}</div>
);

export const NodeHeader: FC<{ id: string; type: AppNodeType; title: string }> = ({ id, title }) => (
    <div>
        <div className={`p-1 bg-slate-600 border-1 border-slate-600 rounded-t-lg text-[6pt] text-white uppercase`}>
            {id}
        </div>
        <div className={`p-1 bg-slate-300 border-slate-600 text-[8pt]`}>{title || '{pageTitle}'}</div>
    </div>
);

export const NodeBody: FC<PropsWithChildren<{ selected: boolean }>> = ({ selected, children }) => (
    <div className={`border-1 border-t-0 ${selected ? 'border-slate-600' : ''} bg-slate-100 rounded-b-lg`}>
        {children}
    </div>
);

export const NodeErrorIcon: FC<{ id: string }> = ({ id }) => {
    const params = useContext(FlowParamsContext);
    const validation = useAtomValue(nodeValidationState({ ...params, nodeId: id }));
    if (validation.state === 'hasData' && validation.data.length > 0) {
        return (
            <div className="absolute top-0 right-0 -mt-2 -mr-2">
                <Tooltip>
                    <TooltipTrigger>
                        <AlertCircle className="text-red-600 h-5 w-5 fill-white" />
                    </TooltipTrigger>
                    <TooltipContent className="text-pretty max-w-sm">
                        <div>
                            <h6 className="text-xs underline">Validation Errors</h6>
                            {validation.data.map((err, index) => (
                                <div key={index} className="font-mono">
                                    • {err}
                                </div>
                            ))}
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>
        );
    }
};

export const NodeDataKeyWarningIcon: FC<{ id: string }> = ({ id }) => {
    const params = useContext(FlowParamsContext);
    const disconnectedLoadable = useAtomValue(disconnectedDataKeysState(params));
    const nodeDisconnected = useMemo(() => {
        if (disconnectedLoadable.state !== 'hasData') return [];
        return disconnectedLoadable.data.filter((d) => d.nodeId === id);
    }, [disconnectedLoadable, id]);

    if (nodeDisconnected.length === 0) return null;

    return (
        <div className="absolute top-0 left-0 -mt-2 -ml-2">
            <Tooltip>
                <TooltipTrigger>
                    <AlertTriangle className="text-amber-500 h-5 w-5 fill-white" />
                </TooltipTrigger>
                <TooltipContent className="text-pretty max-w-sm">
                    <div>
                        <h6 className="text-xs underline">Disconnected Data Keys</h6>
                        {nodeDisconnected.map((d, index) => (
                            <div key={index} className="font-mono">
                                {d.key}
                            </div>
                        ))}
                    </div>
                </TooltipContent>
            </Tooltip>
        </div>
    );
};

export const NodePrompts: FC<{ prompts: { text: string; minimal?: boolean }[] }> = ({ prompts }) =>
    (prompts || []).map((prompt, promptIndex) => (
        <h6 key={promptIndex} className={`p-3 whitespace-pre-line ${prompt.minimal ? 'text-xs italic' : 'text-sm'}`}>
            {prompt.text}
        </h6>
    ));

const ItemTypeDisplay: FC<{
    category: 'equipment' | 'info' | 'consideration';
    id: string;
    unknown?: boolean;
    title?: string;
}> = ({ category, id, unknown, title }) => {
    const { border, text } = getItemTypeColors(unknown ? undefined : category);
    return (
        <div title={title} className={`text-xs border-1 ${border} ${text} bg-white py-1 px-2 rounded-md`}>
            {id}
        </div>
    );
};

export const EquipmentTypeDisplay: FC<{ spaceId: SpaceId; typeId: EquipmentTypeId | '[context]' }> = ({
    spaceId,
    typeId,
}) => {
    const typesById = useAsyncAtomValue(equipmentTypesByIdState(spaceId));
    const type = typeId === '[context]' ? undefined : typesById?.[typeId];
    const missing = typeId !== '[context]' && !!typesById && !type;
    const retired = !!type && !isOfferableType(type);
    const title = missing ? 'Type not found' : retired ? 'Retired type' : undefined;
    return <ItemTypeDisplay category="equipment" id={typeId} unknown={missing || retired} title={title} />;
};

export const InfoTypeDisplay: FC<{ spaceId: SpaceId; typeId: InfoTypeId }> = ({ spaceId, typeId }) => {
    const typesById = useAsyncAtomValue(infoTypesByIdState(spaceId));
    const unknown = typesById && !typesById[typeId];
    return <ItemTypeDisplay category="info" id={typeId} unknown={unknown} />;
};

export const ConsiderationTypeDisplay: FC<{ spaceId: SpaceId; typeId: ConsiderationTypeId }> = ({
    spaceId,
    typeId,
}) => {
    const typesById = useAsyncAtomValue(considerationTypesByIdState(spaceId));
    const unknown = typesById && !typesById[typeId];
    return <ItemTypeDisplay category="consideration" id={typeId} unknown={unknown} />;
};

/**
 * The option list a select field renders as full-width rows, mirroring what the app's guide screen
 * shows: every option, then a "Not Sure" row when the node allows skipping, and no continue button.
 * `SelectNode` keeps its own copy — its rows carry per-option handles, headings and equipment chips.
 */
export const NodeOptionRows: FC<{ options: SelectOption[]; showNotSure?: boolean }> = ({ options, showNotSure }) => (
    <div className="py-2">
        {options.map((option) => (
            <div key={option.value} className="border-t-1 border-gray-300 py-2 bg-white text-xs">
                <div className="flex flex-row items-center justify-center gap-3">{option.title}</div>
                {option.subtitle && <div className="text-xs text-gray-500 italic">{option.subtitle}</div>}
            </div>
        ))}
        {showNotSure && (
            <div className="border-t-1 border-gray-300 py-2 bg-white text-xs text-gray-500 italic text-center">
                Not Sure
            </div>
        )}
        <div className="border-t-1 border-gray-300" />
    </div>
);
